<template>
  <div class="space-y-6">
    <ReportTitle :title="$gettext('Rapport de vente')" :date="report?.attributes?.createdAt" />
    <div v-if="reportData" class="nobreak">
      <h3 class="text-lg font-medium text-gray-800 mb-2">{{ $gettext("Information générale") }}</h3>
      <div class="max-w-md">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt class="font-medium text-gray-700">{{ $gettext("Date de fin") }}:</dt>
          <dd class="text-gray-900 font-bold">
            {{ parseDate(reportData.attributes?.endAt) }}
          </dd>
          <dt class="font-medium text-gray-700">{{ $gettext("Commission") }}:</dt>
          <dd class="text-gray-900 font-bold">
            {{ reportData.attributes?.commission }}
            {{ reportData.relationships?.commissionType?.data?.attributes?.name }}
          </dd>
        </dl>
      </div>
    </div>
    <div v-if="reportData" class="nobreak">
      <div class="max-w-md">
        <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt class="text-gray-700">
            {{ $gettext("Vendeur(s)") }}:
          </dt>
          <dd class="text-gray-900 font-bold">
            <p v-for="(seller, index) in reportData.relationships?.saleListingAgreementSellers?.data"
              :key="index">
              {{ seller?.attributes?.name }}
              <span :class="badgeClassFor(seller?.attributes?.riskScoringLevel)">
                {{ badgeTextFor(seller?.attributes?.riskScoringLevel) }}
              </span>
            </p>
          </dd>
          <dt class="text-gray-700">
            {{ $gettext("Notaire") }}:
          </dt>
          <dd class="text-gray-900 font-bold">
            {{ reportData.relationships?.notary?.data?.attributes?.name }}
            <span v-if="reportData.relationships?.notary?.data?.attributes?.zipcode">
              ({{ reportData.relationships?.notary?.data?.attributes?.zipcode }})
            </span>
          </dd>
        </dl>
      </div>
    </div>
    <div>
      <h3 class="text-lg font-medium text-gray-800 mb-4">{{ $gettext("Propriétés") }}</h3>

      <div v-for="slaProp in saleListingAgreementProperties" :key="slaProp.id"
        class="border rounder-md border-1 border-black p-4">
        <div class="max-w-md">
          <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt class="font-medium text-gray-700">{{ $gettext("Adresse") }}:</dt>
            <dd class="text-gray-900 font-bold">
              {{ slaProp?.relationships?.property?.data?.attributes?.fullAddress }}
            </dd>
            <dt class="font-medium text-gray-700">{{ $gettext("Type de bien") }}:</dt>
            <dd class="text-gray-900 font-bold">
              {{ slaProp?.relationships?.property?.data?.relationships?.propertyType?.data?.attributes?.name }}
            </dd>
            <dt class="font-medium text-gray-700">{{ $gettext("Prix de commercialisation") }}:</dt>
            <dd class="text-gray-900 font-bold">
              {{ slaProp?.attributes?.listingPrice }}€
            </dd>
          </dl>
        </div>

        <div class="mt-4">
          <h5 class="text-lg font-medium text-gray-800 mb-4">{{ $gettext("Offres d'achat") }}</h5>
          <div v-for="offer in slaProp?.relationships?.property?.data?.relationships?.purchaseOffers?.data" :key="offer?.id"
            class="border rounder-md border-1 border-black p-4">

            <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt class="font-medium text-gray-700">{{ $gettext("Date de l'offre") }}:</dt>
              <dd class="text-gray-900 font-bold">
                {{ parseDate(offer?.attributes?.signatureDate) }}
              </dd>
              <dt class="font-medium text-gray-700">{{ $gettext("Montant de l'offre") }}:</dt>
              <dd class="text-gray-900 font-bold">
                {{ offer?.attributes?.price }}€
              </dd>
              <dt class="text-gray-700">
                {{ $gettext("Acheteur(s)") }}:
              </dt>
              <dd class="text-gray-900 font-bold">
                <p v-for="(buyer, index) in offer?.relationships?.buyers?.data" :key="index">
                  {{ buyer?.attributes?.name }}
                  <span :class="badgeClassFor(buyer?.attributes?.riskScoringLevel)">
                    {{ badgeTextFor(buyer?.attributes?.riskScoringLevel) }}
                  </span>
                </p>
              </dd>
            </dl>

            <div v-if="offer?.relationships?.purchaseAgreements?.data?.length" class="mt-4">
              <h5 class="text-lg font-medium text-gray-800 mb-2">{{ $gettext("Compromis de vente") }}</h5>
              <div v-for="agreement in offer?.relationships?.purchaseAgreements?.data" :key="agreement?.id"
                class="border rounder-md border-1 border-black p-4">

                <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <dt class="font-medium text-gray-700">{{ $gettext("Date de signature") }}:</dt>
                  <dd class="text-gray-900 font-bold">
                    {{ parseDate(agreement?.attributes?.signatureDate) }}
                  </dd>
                  <dt class="font-medium text-gray-700">{{ $gettext("Prix final") }}:</dt>
                  <dd class="text-gray-900 font-bold">
                    {{ agreement?.attributes?.finalPrice }}€
                  </dd>
                  <dt class="font-medium text-gray-700">{{ $gettext("Montant de l'accompte") }}:</dt>
                  <dd class="text-gray-900 font-bold">
                    {{ agreement?.attributes?.deposit }}€
                  </dd>
                  <dt class="font-medium text-gray-700">{{ $gettext("Solde") }}:</dt>
                  <dd class="text-gray-900 font-bold">
                    {{ agreement?.attributes?.balance }}
                  </dd>
                  <dt class="font-medium text-gray-700">{{ $gettext("Compte de l'acheteur") }}:</dt>
                  <dd class="text-gray-900 font-bold">
                    {{ agreement?.attributes?.buyerAccountNumber }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ReportRiskScoringBlock :riskScoring="riskScoring" :showSanctionsBlock="false" />
      <ReportDocumentsBlock :documents="documents" />
    </div>
  </div>
</template>

<script setup>
import { useDocumentUtils } from '@/utils/documents';
import ReportTitle from "@/components/reports/ReportTitle.vue";
import ReportDocumentsBlock from '@/components/reports/ReportDocumentsBlock.vue';
import { useGettext } from "vue3-gettext";
import ReportRiskScoringBlock from './ReportRiskScoringBlock.vue';
import { useRiskBadges } from "@/utils/reports_badges.js";
import { useDate } from "@/utils/stores/date";

const { parseDate } = useDate();
const { badgeTextFor, badgeClassFor } = useRiskBadges();
const { $gettext } = useGettext()

const props = defineProps({
  report: {
    type: Object,
    required: true,
  },
});

const reportData = props.report?.attributes?.data;
const saleListingAgreementProperties = reportData?.relationships?.saleListingAgreementProperties?.data || [];
const documents = useDocumentUtils().extractDocuments(props.report);
const riskScoring =
  reportData?.relationships?.lastRiskScoring?.data
    ? reportData.relationships.lastRiskScoring.data
    : null;
</script>
